import React, { type ElementRef, useEffect, useRef, useState, type FC } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { ExitingPersistence, FadeIn, StaggeredEntrance, durations } from '@atlaskit/motion';
import type Select from '@atlaskit/select';

import {
	SpecificAccessRow,
	type SpecificAccessRowProps,
} from './specificAccessRow/SpecificAccessRow';

export type SpecificAccessDetailsProps = {
	principals: SpecificAccessRowProps[];
};

// The modal body already adds 2px of padding.
// Thus to get to 24px of total distance to the footer
// We need 22px of additional padding. There's no token for that :D
// Hence we do padding `space.250` for 20px and another `space.025` of margin for 2px
const specificAccessContainer = xcss({
	paddingBottom: 'space.250',
	marginBottom: 'space.025',
	paddingLeft: 'space.0',
});

export const SpecificAccessDetails: FC<SpecificAccessDetailsProps> = ({ principals }) => {
	const [completedFadeIns, setCompletedFadeIns] = useState(new Set<string>());
	const refElement = useRef<ElementRef<typeof Select>>(null);

	useEffect(() => {
		if (refElement.current) {
			refElement.current.focus();
		}
	}, []);

	return (
		<Stack space="space.100" alignInline="start" grow="fill" xcss={specificAccessContainer} as="ul">
			<ExitingPersistence appear>
				<StaggeredEntrance delayStep={durations.small}>
					{principals.map((specificAccessRowProps, idx) => (
						<FadeIn
							key={specificAccessRowProps.key}
							duration="medium"
							entranceDirection="top"
							exitDirection="top"
							onFinish={() => {
								setCompletedFadeIns((prev) => {
									prev.add(specificAccessRowProps.key);
									return prev;
								});
							}}
							// Only show animation for entrance so that rows after removals are not animated
							isPaused={completedFadeIns.has(specificAccessRowProps.key)}
						>
							{(fadeInProps) => (
								// CSS did not work here, so using style
								<Box
									as="li"
									{...fadeInProps}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
									style={{ listStyle: 'none', width: '100%', ...fadeInProps.style }}
								>
									<SpecificAccessRow
										{...specificAccessRowProps}
										principalAccess={{
											...specificAccessRowProps.principalAccess,
											focusRef: idx === 1 ? refElement : null,
										}}
									/>
								</Box>
							)}
						</FadeIn>
					))}
				</StaggeredEntrance>
			</ExitingPersistence>
		</Stack>
	);
};
